import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import CreateAgentModal from '../components/CreateAgentModal';
import AgentDetailsModal from '../components/AgentDetailsModal';
import { getAgencyById, getAgencyAgents, removeAgent, getAgentInvitations, resendAgentInvitation } from '../services/agencyService';
import { getSubscriptionForAgency } from '../services/subscriptionService';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  PlusIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  TrashIcon,
  UserCircleIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  MapPinIcon,
  EyeIcon,
  PaperAirplaneIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const AgencyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [agents, setAgents] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [showAgentDetailsModal, setShowAgentDetailsModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    loadAgencyData();
  }, [id]);

  const loadAgencyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch agency details
      const agencyData = await getAgencyById(id);
      setAgency(agencyData);

      // Fetch agents for this agency
      try {
        const agentsData = await getAgencyAgents(id);
        setAgents(Array.isArray(agentsData) ? agentsData : agentsData.data || []);
      } catch (agentErr) {
        console.warn('Could not load agents:', agentErr);
        setAgents([]);
      }

      // Fetch pending invitations
      try {
        const invData = await getAgentInvitations(id);
        setPendingInvitations(invData);
      } catch (invErr) {
        console.warn('Could not load invitations:', invErr);
        setPendingInvitations([]);
      }

      // Fetch subscription
      try {
        const sub = await getSubscriptionForAgency(parseInt(id));
        setSubscription(sub);
      } catch (subErr) {
        console.warn('Could not load subscription:', subErr);
      }
    } catch (err) {
      console.error('Error loading agency:', err);
      setError(err.message || 'Failed to load agency details');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAgent = async (agentId, agentName) => {
    if (window.confirm(`Are you sure you want to remove "${agentName}" from this agency?`)) {
      try {
        await removeAgent(id, agentId);
        loadAgencyData();
      } catch (err) {
        console.error('Error removing agent:', err);
        alert(err.message || 'Failed to remove agent');
      }
    }
  };

  const handleCreateAgentSuccess = () => {
    setShowCreateAgentModal(false);
    loadAgencyData();
  };

  const handleResendInvitation = async (invitation) => {
    setResendingId(invitation.id);
    try {
      await resendAgentInvitation(id, invitation.id);
      alert(`Invitation re-sent to ${invitation.email}.`);
      loadAgencyData();
    } catch (err) {
      console.error('Error resending invitation:', err);
      alert(err.message || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const handleViewAgent = (agent) => {
    setSelectedAgent(agent);
    setShowAgentDetailsModal(true);
  };

  const handleCloseAgentDetails = () => {
    setSelectedAgent(null);
    setShowAgentDetailsModal(false);
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading agency details...</p>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-20">
              <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md mx-auto">
                <div className="text-red-400 mb-4">
                  <BuildingOfficeIcon className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-red-900 mb-2">Error Loading Agency</h3>
                <p className="text-red-700 mb-4">{error}</p>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => navigate('/agencies')}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Back to Agencies
                  </button>
                  <button
                    onClick={loadAgencyData}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/agencies"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Agencies
          </Link>

          {/* Agency Header */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-8">
              <div className="flex items-center">
                <div className="bg-white/20 p-4 rounded-xl">
                  <BuildingOfficeIcon className="h-10 w-10 text-white" />
                </div>
                <div className="ml-4">
                  <h1 className="text-2xl font-bold text-white">{agency?.name}</h1>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {agency?.currency && (
                      <span className="inline-flex items-center bg-white/20 px-3 py-1 rounded-full text-sm text-white">
                        <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                        {agency.currency}
                      </span>
                    )}
                    {subscription && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize
                        ${subscription.plan === 'gold'   ? 'bg-yellow-400 text-yellow-900' :
                          subscription.plan === 'silver' ? 'bg-slate-200 text-slate-800' :
                          subscription.plan === 'bronze' ? 'bg-amber-500 text-white' :
                                                           'bg-white/20 text-white'}`}>
                        <CreditCardIcon className="h-4 w-4 mr-1" />
                        {subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)} Plan
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {agency?.address && (
                  <div className="flex items-start">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Address</div>
                      <div className="text-gray-900">{agency.address}</div>
                    </div>
                  </div>
                )}
                {agency?.contact_email && (
                  <div className="flex items-start">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Email</div>
                      <div className="text-gray-900">{agency.contact_email}</div>
                    </div>
                  </div>
                )}
                {agency?.contact_phone && (
                  <div className="flex items-start">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Phone</div>
                      <div className="text-gray-900">{agency.contact_phone}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Agents Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Agents</h2>
                <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-sm">
                  {agents.length}
                </span>
                {pendingInvitations.length > 0 && (
                  <span className="ml-2 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-sm">
                    {pendingInvitations.length} pending
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowCreateAgentModal(true)}
                className="inline-flex items-center px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors text-sm"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Agent
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Pending Invitations */}
              {pendingInvitations.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1.5" />
                    Pending Invitations
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pendingInvitations.map((inv) => (
                      <div
                        key={inv.id}
                        className="bg-amber-50 border border-amber-200 rounded-xl p-5"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="bg-amber-400 p-2.5 rounded-xl flex-shrink-0">
                              <EnvelopeIcon className="h-6 w-6 text-white" />
                            </div>
                            <div className="ml-3 flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-gray-900 truncate">
                                {inv.given} {inv.surname}
                              </h4>
                              <p className="text-xs text-gray-500 truncate" title={inv.email}>
                                {inv.email}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mb-3">
                          {inv.is_expired ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Expired
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              Awaiting acceptance
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            {inv.is_expired
                              ? 'Expired'
                              : `Expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                          </span>
                        </div>

                        <button
                          onClick={() => handleResendInvitation(inv)}
                          disabled={resendingId === inv.id}
                          className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                          {resendingId === inv.id ? 'Sending…' : 'Resend Invitation'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Agents */}
              {agents.length === 0 && pendingInvitations.length === 0 ? (
                <div className="text-center py-12">
                  <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No agents yet</h3>
                  <p className="mt-2 text-gray-500">Add agents to manage properties for this agency.</p>
                  <button
                    onClick={() => setShowCreateAgentModal(true)}
                    className="mt-4 inline-flex items-center px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add First Agent
                  </button>
                </div>
              ) : agents.length > 0 ? (
                <div>
                  {pendingInvitations.length > 0 && (
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center">
                      <UserGroupIcon className="h-4 w-4 mr-1.5" />
                      Active Agents
                    </h3>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {agents.map((agent) => (
                      <div
                        key={agent.ID || agent.id}
                        className="bg-white border border-gray-200 rounded-xl p-5 hover:border-emerald-300 hover:shadow-md transition-all duration-200"
                      >
                        {/* Agent Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-2.5 rounded-xl shadow-lg shadow-emerald-500/20 flex-shrink-0">
                              <UserCircleIcon className="h-6 w-6 text-white" />
                            </div>
                            <div className="ml-3 flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-gray-900 truncate">
                                {agent.User?.given || 'Unknown'} {agent.User?.surname || ''}
                              </h4>
                              <p className="text-xs text-gray-500 truncate" title={agent.User?.email || 'No email available'}>
                                {agent.User?.email || 'No email available'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                            <button
                              onClick={() => handleViewAgent(agent)}
                              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="View Agent Details"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveAgent(
                                agent.ID || agent.id,
                                `${agent.User?.given || ''} ${agent.User?.surname || ''}`.trim() || 'this agent'
                              )}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove Agent"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Agent Status */}
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            Agent
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            agent.User?.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {agent.User?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        {/* Quick Actions */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <button
                            onClick={() => handleViewAgent(agent)}
                            className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                          >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Create Agent Modal */}
      <CreateAgentModal
        show={showCreateAgentModal}
        onClose={() => setShowCreateAgentModal(false)}
        onSuccess={handleCreateAgentSuccess}
        agencyId={id}
        agencyName={agency?.name}
      />

      {/* Agent Details Modal */}
      <AgentDetailsModal
        show={showAgentDetailsModal}
        onClose={handleCloseAgentDetails}
        agent={selectedAgent}
        agency={agency}
      />
    </PageWrapper>
  );
};

export default AgencyDetails;
